import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface TomaInfo {
  tomaId: number;
  fecha: string;
  tableName: string;
}

interface MatrixCell {
  value: number;
  count: number;
}

interface CategoryPriceTrendData {
  tomas: TomaInfo[];
  categories: string[];
  matrix: { [category: string]: { [tomaId: number]: MatrixCell | null } };
  skuMatrix?: { [category: string]: { [tomaId: number]: { sku: string; price: number }[] } } | null;
  analysisLevel: 'listings' | 'skus';
  metric: 'average' | 'median';
  priceType: 'current' | 'original';
  baselineTomaId: number | null;
}

interface CategoryPriceTimelineProps {
  data: CategoryPriceTrendData;
  formatPrice: (price: number) => string;
  formatDate: (dateStr: string) => string;
  formatDateFull: (dateStr: string) => string;
  analysisLevel: 'listings' | 'skus';
  baselineTomaId: number | null;
  getUnitLabel: (count: number, plural?: boolean) => string;
}

// Color palette for lines
const COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
];

const CategoryPriceTimeline: React.FC<CategoryPriceTimelineProps> = ({
  data,
  formatPrice,
  formatDate,
  formatDateFull,
  baselineTomaId,
  getUnitLabel
}) => {
  const { t } = useTranslation();

  // State for selected categories
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => {
    // Default: select top 5 categories
    return new Set(data.categories.slice(0, 5));
  });

  // State for search
  const [searchTerm, setSearchTerm] = useState('');

  // Toggle category selection
  const toggleCategory = (category: string) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      if (newSelected.size < 10) {
        newSelected.add(category);
      }
    }
    setSelectedCategories(newSelected);
  };

  // Select all / deselect all
  const handleSelectAll = () => {
    if (selectedCategories.size === data.categories.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(data.categories.slice(0, 10)));
    }
  };

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return data.categories;
    const lowerSearch = searchTerm.toLowerCase();
    return data.categories.filter(cat => cat.toLowerCase().includes(lowerSearch));
  }, [data.categories, searchTerm]);

  // Get baseline toma info
  const baselineToma = useMemo(() => {
    if (!baselineTomaId) return data.tomas[0] || null;
    return data.tomas.find(t => t.tomaId === baselineTomaId) || data.tomas[0] || null;
  }, [data.tomas, baselineTomaId]);

  // Get the unit label for display
  const getCountLabel = (count: number): string => {
    const unitLabel = getUnitLabel(count, count !== 1);
    return `${count.toLocaleString('es-AR')} ${unitLabel}`; // PT-#87 PR-d: miles es-AR ("1.380", no "1380")
  };

  // #12 — Mapa de color ESTABLE por nombre de serie. La clave es el índice de la
  // categoría/marca en `data.categories` (orden estable del backend), NO el índice
  // dentro de la selección. Así cada serie conserva su color aunque se deseleccionen
  // otras (antes: al sacar una serie, todas las de abajo cambiaban de color).
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    data.categories.forEach((cat, i) => {
      map[cat] = COLORS[i % COLORS.length];
    });
    return map;
  }, [data.categories]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return data.tomas.map(toma => {
      const point: { [key: string]: any } = {
        fecha: formatDate(toma.fecha),
        fechaFull: toma.fecha,
        tomaId: toma.tomaId
      };

      Array.from(selectedCategories).forEach(category => {
        const cell = data.matrix[category]?.[toma.tomaId];
        if (cell) {
          point[category] = cell.value;
          point[`${category}_count`] = cell.count;
        }
      });

      return point;
    });
  }, [data, selectedCategories, formatDate]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    // #11 — Formato "Serie (N)" a la izquierda, "$XXX" alineado a la derecha.
    // El conteo (N) va pegado al nombre de la serie; el precio queda solo y en
    // tabular-nums para que alinee verticalmente entre filas.
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg max-w-sm">
        <p className="font-semibold text-gray-800 mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => {
            const category = entry.dataKey;
            const countKey = `${category}_count`;
            const count = entry.payload[countKey];

            return (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center space-x-2 min-w-0">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-700 truncate">
                    {category}
                    {count != null && (
                      <span className="text-xs text-gray-500 ml-1">({count})</span>
                    )}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 tabular-nums flex-shrink-0">
                  {formatPrice(entry.value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Calculate change vs baseline for a category
  const calculateChangeVsBaseline = (category: string): { change: number | null; baselineValue: number | null; currentValue: number | null } => {
    if (!baselineToma) return { change: null, baselineValue: null, currentValue: null };

    const baselineCell = data.matrix[category]?.[baselineToma.tomaId];
    const lastToma = data.tomas[data.tomas.length - 1];
    const currentCell = data.matrix[category]?.[lastToma?.tomaId];

    if (!baselineCell || !currentCell || baselineCell.value === 0) {
      return { change: null, baselineValue: baselineCell?.value || null, currentValue: currentCell?.value || null };
    }

    const change = ((currentCell.value - baselineCell.value) / baselineCell.value) * 100;
    return { change, baselineValue: baselineCell.value, currentValue: currentCell.value };
  };

  return (
    <div className="space-y-4">
      {/* Category selector */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">
            {t('statistics.category_price_trend.select_categories')}
          </h4>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">
              {selectedCategories.size}/10 {t('statistics.category_price_trend.selected')}
            </span>
            <button
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {selectedCategories.size > 0
                ? t('statistics.category_price_trend.deselect_all')
                : t('statistics.category_price_trend.select_top')}
            </button>
          </div>
        </div>

        {/* Search */}
        {data.categories.length > 10 && (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('statistics.category_price_trend.search_categories')}
            className="w-full px-3 py-1.5 mb-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )}

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {filteredCategories.map((category, index) => {
            const isSelected = selectedCategories.has(category);
            const color = colorMap[category];

            return (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                disabled={!isSelected && selectedCategories.size >= 10}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  isSelected
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                style={isSelected ? { backgroundColor: color } : {}}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      {selectedCategories.size > 0 ? (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="fecha"
                tick={{ fontSize: 11 }}
                tickMargin={10}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                  return `$${value}`;
                }}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value: string) => (
                  <span className="text-xs text-gray-700">{value}</span>
                )}
              />
              {Array.from(selectedCategories).map((category) => (
                <Line
                  key={category}
                  type="monotone"
                  dataKey={category}
                  stroke={colorMap[category]}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            {t('statistics.category_price_trend.select_at_least_one')}
          </p>
        </div>
      )}

      {/* Stats summary with baseline reference */}
      {selectedCategories.size > 0 && baselineToma && (
        <>
          <div className="text-sm text-gray-500 text-center">
            {t('statistics.category_price_trend.baseline_reference')}: {formatDateFull(baselineToma.fecha)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from(selectedCategories).slice(0, 4).map((category) => {
              const { change, currentValue } = calculateChangeVsBaseline(category);
              const lastToma = data.tomas[data.tomas.length - 1];
              const currentCell = data.matrix[category]?.[lastToma?.tomaId];

              return (
                <div
                  key={category}
                  className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: colorMap[category] }}
                    />
                    <span className="text-xs font-medium text-gray-700 truncate">
                      {category}
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {currentValue ? formatPrice(currentValue) : '—'}
                  </div>
                  {currentCell && (
                    <div className="text-xs text-gray-500">
                      {getCountLabel(currentCell.count)}
                    </div>
                  )}
                  <div className={`text-xs mt-1 ${
                    change === null ? 'text-gray-400' :
                    change > 0 ? 'text-red-500' :
                    change < 0 ? 'text-green-500' :
                    'text-gray-500'
                  }`}>
                    {change === null ? '—' : (
                      <>
                        {change > 0 ? '+' : ''}{change.toFixed(1)}%
                        {' '}
                        {t('statistics.category_price_trend.vs_baseline')}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryPriceTimeline;
